// src/utils/imageUtils.js — Compresión de imagen en cliente
// La imagen se procesa localmente. NUNCA se envía al servidor como archivo.
// Solo el base64 comprimido se transmite al Worker para análisis efímero.

const MAX_DIMENSION = 1024;  // px — máximo ancho o alto
const JPEG_QUALITY  = 0.85;  // 0.0–1.0

/**
 * compressImage
 * Redimensiona y comprime un File/Blob a JPEG base64.
 *
 * @param {File|Blob} file
 * @returns {Promise<{ base64: string, dataUrl: string, width: number, height: number, sizeKB: number }>}
 */
export function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Calcular dimensiones finales manteniendo aspect ratio
        let { width, height } = img;

        if (width > height) {
          if (width > MAX_DIMENSION) {
            height = Math.round(height * MAX_DIMENSION / width);
            width  = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width  = Math.round(width  * MAX_DIMENSION / height);
            height = MAX_DIMENSION;
          }
        }

        // Dibujar en canvas y comprimir a JPEG
        const canvas = document.createElement('canvas');
        canvas.width  = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
        const base64  = dataUrl.split(',')[1]; // Sin header "data:image/jpeg;base64,"
        const sizeKB  = Math.round((base64.length * 3 / 4) / 1024);

        resolve({ base64, dataUrl, width, height, sizeKB });
      };

      img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsDataURL(file);
  });
}

/**
 * validateImageFile
 * Valida tipo y tamaño del archivo antes de procesar.
 *
 * @param {File} file
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateImageFile(file) {
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const MAX_SIZE_MB   = 15;

  if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(jpe?g|png|webp|heic|heif)$/i)) {
    return { valid: false, error: 'Formato no soportado. Usa JPEG, PNG o WebP.' };
  }

  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    return { valid: false, error: `Imagen demasiado grande. Máximo ${MAX_SIZE_MB}MB.` };
  }

  return { valid: true };
}
